import { Square, Hourglass, CheckCircle, Ban } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * The four exclusive states of a combination (D2, migration 132) — icon, label, colour and
 * order, in ONE place.
 *
 * Every surface reads this map: the list's Status cell, the six stat tiles, the filter pills,
 * the drawer's segment control and the Customer-360 Lost-Business table. A second copy is how
 * the same combination ends up green in one view and grey in another.
 *
 * NOT to be confused with the Customer-360 tab's own `Status` column
 * (lost / overdue / watch / active). That one is the derived ORDER-CYCLE state — a different
 * question, kept deliberately separate; see api/.../shared/customer-scope.ts.
 */
export type CbStatus = 'Open' | 'InProgress' | 'Checked' | 'Lost';

export interface CbStatusMeta {
    label: string;
    /** Icon colour in the list and the drawer. */
    icon: LucideIcon;
    iconClass: string;
    /** Pill / badge colouring where the state is shown as a chip. */
    chipClass: string;
    /** Tile accent when the state is one of the clickable KPI cards. */
    tileClass: string;
}

export const CB_STATUS_ORDER: CbStatus[] = ['Open', 'InProgress', 'Checked', 'Lost'];

export const CB_STATUS_META: Record<CbStatus, CbStatusMeta> = {
    Open: {
        label: 'Open',
        icon: Square,
        iconClass: 'text-gray-400',
        chipClass: 'bg-gray-100 text-gray-600',
        tileClass: 'text-gray-500',
    },
    InProgress: {
        label: 'In progress',
        icon: Hourglass,
        iconClass: 'text-blue-600',
        chipClass: 'bg-blue-100 text-blue-700',
        tileClass: 'text-blue-600',
    },
    Checked: {
        label: 'Checked',
        icon: CheckCircle,
        iconClass: 'text-green-600',
        chipClass: 'bg-green-100 text-green-700',
        tileClass: 'text-green-600',
    },
    Lost: {
        label: 'Lost',
        icon: Ban,
        iconClass: 'text-gray-400',
        chipClass: 'bg-gray-100 text-gray-500',
        tileClass: 'text-gray-400',
    },
};

/** A status coming off the wire, coerced to a known one. Absent / unknown reads as Open. */
export function toCbStatus(raw: string | null | undefined): CbStatus {
    return raw && raw in CB_STATUS_META ? raw as CbStatus : 'Open';
}
